/**
 * BroadPaper document format — schemaVersion 1.
 *
 * These types describe the saved JSON template. They are a public API:
 * every change must be additive or go through a migration in `schema/migrate.ts`.
 *
 * Units: page geometry (size, margins, region heights) is stored in millimetres
 * because that is how people think about paper. All other lengths (font sizes,
 * padding, borders) are CSS pixels at 96 dpi.
 */

import type { BlockStyle } from "./style.js";

export const CURRENT_SCHEMA_VERSION = 1 as const;
export type SchemaVersion = typeof CURRENT_SCHEMA_VERSION;

/** A colour literal (`#RRGGBB`, `rgba(...)`) or a theme token reference (`$colors.primary`). */
export type ColorValue = string;
/** A font family literal (`"Inter"`) or a theme token reference (`$typography.headingFont`). */
export type FontValue = string;
/** A theme token reference. Always starts with `$`. */
export type TokenRef = `$${string}`;

export type PageSizeName = "A4" | "A3" | "A5" | "Letter" | "Legal" | "Tabloid";
export interface CustomPageSize {
  /** Width in millimetres. */
  width: number;
  /** Height in millimetres. */
  height: number;
}
export type PageSize = PageSizeName | CustomPageSize;
export type PageOrientation = "portrait" | "landscape";

export interface PageMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface PageRegionSettings {
  /** Region height in millimetres (0 disables the region). */
  height: number;
  /** Use `header.firstPage` / `footer.firstPage` content on page 1. */
  differentFirstPage: boolean;
}

export interface PageSettings {
  size: PageSize;
  orientation: PageOrientation;
  margins: PageMargins;
  header: PageRegionSettings;
  footer: PageRegionSettings;
  background?: ColorValue;
  /** Number of pages to show as the design canvas when the document is empty. */
  minPages?: number;
}

export type TextStyleId = "h1" | "h2" | "h3" | "h4" | "body" | "lead" | "caption" | "small" | (string & {});

export interface TextStyle {
  fontFamily?: FontValue;
  fontSize?: number;
  fontWeight?: number | "normal" | "bold";
  fontStyle?: "normal" | "italic";
  lineHeight?: number;
  letterSpacing?: number;
  textTransform?: "none" | "uppercase" | "lowercase" | "capitalize";
  color?: ColorValue;
  marginTop?: number;
  marginBottom?: number;
  /** Headings default to keep-with-next. */
  keepWithNext?: boolean;
}

export interface TableStyle {
  fontSize?: number;
  headerBackground?: ColorValue;
  headerColor?: ColorValue;
  headerFontWeight?: number | "normal" | "bold";
  borderColor?: ColorValue;
  borderWidth?: number;
  /** "all" draws a grid, "horizontal" draws row rules, "none" draws no lines. */
  borders?: "all" | "horizontal" | "outer" | "none";
  zebraBackground?: ColorValue;
  cellPaddingX?: number;
  cellPaddingY?: number;
}

export interface CardStyle {
  background?: ColorValue;
  borderColor?: ColorValue;
  borderWidth?: number;
  borderRadius?: number;
  padding?: number;
  shadow?: boolean;
}

export interface DocumentStyles {
  text: Record<TextStyleId, TextStyle>;
  table: TableStyle;
  card: CardStyle;
  link: { color?: ColorValue; underline?: boolean };
  spacing: {
    /** Vertical gap between blocks in px. */
    blockGap: number;
    /** Default section vertical padding in px. */
    sectionPadding: number;
    /** Gap between columns in px. */
    columnGap: number;
  };
}

/** Structured visibility rule, built by the UI without writing code. */
export interface ConditionRule {
  /** Left operand: an expression, usually a data path such as `client.isRetired`. */
  left: string;
  operator:
    | "eq"
    | "neq"
    | "gt"
    | "gte"
    | "lt"
    | "lte"
    | "contains"
    | "startsWith"
    | "endsWith"
    | "in"
    | "isEmpty"
    | "isNotEmpty"
    | "isTrue"
    | "isFalse";
  /** Right operand as a literal (string, number, boolean) or an expression string wrapped in `{{ }}`. */
  right?: string | number | boolean | null;
}
export interface ConditionGroup {
  op: "and" | "or";
  rules: Array<ConditionRule | ConditionGroup>;
}
/** A raw expression string or a structured group. Both compile to the expression language. */
export type Condition = string | ConditionGroup;

export type BreakMode = "auto" | "page";

export interface FlowOptions {
  breakBefore?: BreakMode;
  breakAfter?: BreakMode;
  /** Never split this node across pages unless it is taller than a page. */
  keepTogether?: boolean;
  /** Keep this node on the same page as the start of the next sibling. */
  keepWithNext?: boolean;
  /** Minimum lines/rows to leave at the bottom of a page (orphans). */
  minOrphans?: number;
  /** Minimum lines/rows to carry to the next page (widows). */
  minWidows?: number;
}

/**
 * A node in the document tree. Structural node types are `section`, `row` and
 * `column`; everything else is a block type resolved through the block registry.
 */
export interface Node {
  id: string;
  type: string;
  props: Record<string, unknown>;
  style?: BlockStyle;
  children?: Node[];
  visibleWhen?: Condition;
  flow?: FlowOptions;
  /** Human label shown in the layer tree. */
  name?: string;
  /** A locked node cannot be moved or deleted in the editor. */
  locked?: boolean;
}

export interface RegionContent {
  default: Node[];
  firstPage?: Node[];
}

export interface LibraryItem {
  id: string;
  name: string;
  description?: string;
  category?: string;
  /** A section (or block) subtree to insert. IDs are re-generated on insert. */
  node: Node;
  thumbnail?: string;
}

export interface TemplateSettings {
  /** BCP-47 locale used for number/date formatting defaults. */
  locale?: string;
  /** ISO 4217 currency used when `currency` pipe has no explicit code. */
  currency?: string;
  /** IANA time zone used for date formatting. */
  timeZone?: string;
  /** Data source ids this template was designed against. */
  dataSources?: string[];
}

export interface TemplateMeta {
  createdAt?: string;
  updatedAt?: string;
  author?: string;
  description?: string;
  tags?: string[];
  /** Free-form host metadata. Never interpreted by BroadPaper. */
  custom?: Record<string, unknown>;
}

export interface ReportTemplate {
  schemaVersion: SchemaVersion;
  id: string;
  name: string;
  meta?: TemplateMeta;
  page: PageSettings;
  styles: DocumentStyles;
  header: RegionContent;
  footer: RegionContent;
  /** Top-level content: a list of `section` nodes. */
  body: Node[];
  library?: LibraryItem[];
  settings?: TemplateSettings;
}

/** A template of any historical schema version. Use `migrateTemplate` before use. */
export interface AnyVersionTemplate {
  schemaVersion: number;
  [key: string]: unknown;
}
